import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = [
        "hiddenInput",
        "mappingTemplate",
        "mappingsDisplay",
        "configurationTypeSelect",
        "fieldSelect"
    ];

    static values = {
        deviceTypeFields: Object,
        selections: Object,
        configurationTypes: Array
    };

    connect() {
        const initialSelections = this.selectionsValue || {};
        this.selections = {};
        this.noKeyFieldsMessage = "No key fields configured for this type in Halo";

        // Copy selections using type_id as keys (keys from JSONB are already strings)
        Object.keys(initialSelections).forEach(typeId => {
            this.selections[String(typeId)] = initialSelections[typeId];
        });

        this.mappingCounter = 0;
        Object.keys(this.selections).forEach(typeId => {
            const fieldValue = this.selections[typeId];
            const configType = this.configurationTypesValue.find(ct => String(ct.type_id) === String(typeId));
            if (configType) {
                this.createMappingFromTemplate(this.mappingCounter++, {
                    configuration_type_id: configType.id, // Use id for the select value
                    field_value: fieldValue
                });
            }
        });

        this.updateHiddenInput();
    }

    addMapping(event) {
        event.preventDefault();

        if (this.configurationTypesValue.length === 0) {
            alert("No configuration types available");
            return;
        }

        const newIndex = this.mappingCounter++;
        const firstConfigType = this.configurationTypesValue[0];
        this.createMappingFromTemplate(newIndex, {
            configuration_type_id: firstConfigType.id,
            field_value: "" // Will be set to first field by updateConfigurationType
        });
    }

    removeMapping(event) {
        event.preventDefault();

        if (!confirm("Are you sure?")) {
            return;
        }

        const mappingIndex = event.target.dataset.mappingIndex || event.target.parentElement.dataset.mappingIndex;
        const mappingSection = this.findMappingSection(mappingIndex);

        if (mappingSection) {
            const configTypeSelect = this.findMappingTarget("configurationTypeSelect", mappingIndex);
            if (configTypeSelect && configTypeSelect.value) {
                const selectedOption = configTypeSelect.options[configTypeSelect.selectedIndex];
                const typeId = selectedOption ? selectedOption.dataset.typeId : null;
                if (typeId) {
                    delete this.selections[String(typeId)];
                    this.updateHiddenInput();
                }
            }

            mappingSection.remove();
        }
    }

    updateConfigurationType(event) {
        const mappingIndex = event.target.dataset.mappingIndex;
        const configTypeSelect = event.target;
        const oldConfigTypeId = configTypeSelect.dataset.previousValue || "";
        const newConfigTypeId = configTypeSelect.value;

        // Store the new value for next time
        configTypeSelect.dataset.previousValue = newConfigTypeId;

        // Remove old selection if configuration type changed
        if (oldConfigTypeId && oldConfigTypeId !== newConfigTypeId) {
            // Find the old option to get its type_id
            const oldOption = Array.from(configTypeSelect.options).find(opt => opt.value === oldConfigTypeId);
            if (oldOption && oldOption.dataset.typeId) {
                delete this.selections[String(oldOption.dataset.typeId)];
            }
        }

        const fieldSelect = this.findMappingTarget("fieldSelect", mappingIndex);

        // Clear the field select and populate it based on the selected configuration type
        if (fieldSelect) {
            fieldSelect.innerHTML = "";

            if (newConfigTypeId) {
                const configType = this.configurationTypesValue.find(ct => String(ct.id) === String(newConfigTypeId));
                if (configType) {
                    // Try to get fields by type_id first, then by configuration_type.id
                    const typeIdKey = configType.type_id ? String(configType.type_id) : null;
                    let fields = [];

                    if (typeIdKey && this.deviceTypeFieldsValue[typeIdKey]) {
                        fields = this.deviceTypeFieldsValue[typeIdKey];
                    } else if (this.deviceTypeFieldsValue[String(configType.id)]) {
                        fields = this.deviceTypeFieldsValue[String(configType.id)];
                    }

                    if (fields.length === 0) {
                        // Fill the field select with the default field
                        const option = document.createElement('option');
                        option.textContent = this.noKeyFieldsMessage;
                        fieldSelect.appendChild(option);
                    } else {
                        // Populate field options
                        fields.forEach(field => {
                            const option = document.createElement('option');
                            // Expect field to be an object with 'id' and 'name' properties
                            const isObject = typeof field === 'object' && field !== null && !Array.isArray(field);
                            const fieldId = isObject ? field.id : null;
                            const fieldName = isObject ? field.name : null;
                            const fieldValue = fieldId || fieldName || field;
                            option.value = fieldValue;
                            option.textContent = fieldName || fieldId || field;
                            fieldSelect.appendChild(option);
                        });
                    }

                    // Select the first field by default if available
                    if (fields.length > 0 && fieldSelect.options.length > 0) {
                        fieldSelect.value = fieldSelect.options[0].value;
                    }
                }
            }
        }

        // Update selections - remove old selection if config type changed
        this.updateSelectionsForMapping(mappingIndex);
    }

    updateFieldSelection(event) {
        const mappingIndex = event.target.dataset.mappingIndex;
        this.updateSelectionsForMapping(mappingIndex);
    }

    updateSelectionsForMapping(mappingIndex) {
        const configTypeSelect = this.findMappingTarget("configurationTypeSelect", mappingIndex);
        const fieldSelect = this.findMappingTarget("fieldSelect", mappingIndex);

        if (configTypeSelect && fieldSelect) {
            const configTypeId = configTypeSelect.value;
            const fieldValue = fieldSelect.value;

            if (configTypeId && fieldValue && fieldValue !== this.noKeyFieldsMessage) {
                // Get the type_id from the selected option
                const selectedOption = configTypeSelect.options[configTypeSelect.selectedIndex];
                const typeId = selectedOption ? selectedOption.dataset.typeId : null;
                if (typeId) {
                    this.selections[String(typeId)] = fieldValue;
                }
            } else if (configTypeId) {
                // Remove selection if field is cleared
                const selectedOption = configTypeSelect.options[configTypeSelect.selectedIndex];
                const typeId = selectedOption ? selectedOption.dataset.typeId : null;
                if (typeId) {
                    delete this.selections[String(typeId)];
                }
            }

            this.updateHiddenInput();
        }
    }

    createMappingFromTemplate(index, mappingData) {
        const template = this.mappingTemplateTarget;
        const clone = template.content.cloneNode(true);

        const section = clone.querySelector('.name-selector__mapping');
        this.updateTemplateIndexes(section, index);

        this.mappingsDisplayTarget.appendChild(section);

        const configTypeSelect = this.findMappingTarget("configurationTypeSelect", index);
        const fieldSelect = this.findMappingTarget("fieldSelect", index);

        if (configTypeSelect && mappingData.configuration_type_id) {
            configTypeSelect.value = mappingData.configuration_type_id;
            configTypeSelect.dataset.previousValue = mappingData.configuration_type_id;
            this.updateConfigurationType({ target: configTypeSelect });

            // Set field value after dropdown is populated (if a specific value was provided)
            if (mappingData.field_value && fieldSelect) {
                fieldSelect.value = mappingData.field_value;
                this.updateSelectionsForMapping(index);
            }
        }
    }

    findMappingTarget(targetName, mappingIndex) {
        const targets = this[`${targetName}Targets`];
        return targets.find(target => target.dataset.mappingIndex === mappingIndex.toString());
    }

    findMappingSection(mappingIndex) {
        return this.mappingsDisplayTarget.querySelector(`[data-mapping-index="${mappingIndex}"]`);
    }

    updateTemplateIndexes(element, index) {
        const elementsWithIndex = element.querySelectorAll('[data-mapping-index="TEMPLATE_INDEX"]');
        elementsWithIndex.forEach(el => {
            el.dataset.mappingIndex = index;
        });
        if (element.dataset && element.dataset.mappingIndex === "TEMPLATE_INDEX") {
            element.dataset.mappingIndex = index;
        }
    }

    updateHiddenInput() {
        const jsonValue = JSON.stringify(this.selections);
        this.hiddenInputTarget.value = jsonValue;
    }
}
